import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  FormHelperText,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";

import {
  getPetitionCount,
  signPetition,
} from "../../apis/user-apis/petitionSectionApis";

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

const PetitionSection = () => {
  const [count, setCount] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zipCode: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    zipCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Inline message replaces top-right Snackbar
  const [inlineMsg, setInlineMsg] = useState({
    show: false,
    text: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchCount = async () => {
    try {
      const data = await getPetitionCount();
      setCount(data.count);
    } catch (error) {}
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "name" ? filterName(value) : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const tempErrors = { name: "", email: "", phone: "", zipCode: "" };
    let isValid = true;
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      tempErrors.name = "Name is required";
      isValid = false;
    } else if (nameTrimmed.split(/\s+/).filter(Boolean).length < 2) {
      tempErrors.name = "Please enter full name";
      isValid = false;
    } else if (!NAME_REGEX.test(nameTrimmed)) {
      tempErrors.name = "Name should contain only letters";
      isValid = false;
    }
    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      tempErrors.email = "Invalid email address";
      isValid = false;
    }
    if (formData.phone.trim() && !/^\d{10}$/.test(formData.phone.trim())) {
      tempErrors.phone = "Phone must be 10 digits";
      isValid = false;
    }
    if (!formData.zipCode.trim()) {
      tempErrors.zipCode = "Zip code is required";
      isValid = false;
    } else if (!/^\d{1,6}$/.test(formData.zipCode.trim())) {
      tempErrors.zipCode = "Zip code is not valid";
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!validate()) return;
    try {
      setLoading(true);
      const response = await signPetition(formData);
      // Show inline message right below the button — not top-right corner
      setInlineMsg({ show: true, text: response.message, severity: "success" });
      fetchCount();
      setFormData({ name: "", email: "", phone: "", zipCode: "" });
      setErrors({ name: "", email: "", phone: "", zipCode: "" });
      setHasSubmitted(false);
      setTimeout(
        () => setInlineMsg((prev) => ({ ...prev, show: false })),
        6000,
      );
    } catch (error) {
      setInlineMsg({
        show: true,
        text: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    // id="petition" allows auto scroll from nav and other buttons
    <Box
      id="petition"
      sx={{
        backgroundColor: "#F9F7F2",
        display: "flex",
        alignItems: "center",
        py: 10,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: "#0A192F",
            borderRadius: "12px",
            p: { xs: 2.5, sm: 5, md: 8 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 6 },
            alignItems: "flex-start",
          }}
        >
          {/* ── Left Content ──────────────────────────────────── */}
          <Box sx={{ flex: 1.2 }}>
            <Typography
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Community Action
            </Typography>

            <Typography
              sx={{
                color: "#fff",
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "1.4rem", sm: "2.2rem", md: "3.5rem" },
                fontWeight: 600,
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              Stop the{" "}
              <Box
                component="span"
                sx={{ color: "#C59324", fontStyle: "italic", fontWeight: 500 }}
              >
                No-Vote Tax Increase
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "#B8C0D1",
                fontSize: "1.1rem",
                lineHeight: 1.8,
                mb: 6,
              }}
            >
              The Appoquinimink School Board raised property taxes 10% in 2025
              without holding a public referendum by using a legal loophole to
              bypass the community. Delaware State Auditor Lydia York confirmed
              years of systemic budgeting failures caused this problem.
              Middletown residents deserve better and deserve accountability.
            </Typography>

            {/* Progress */}
            <Box
              sx={{
                pt: 4,
                borderTop: "1px solid rgba(255,255,255,0.1)",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 4,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#C59324",
                    fontSize: "2.5rem",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  sx={{ color: "#B8C0D1", fontSize: "0.85rem", mt: 1 }}
                >
                  District 8 residents have signed
                </Typography>
              </Box>
              <Box sx={{ flex: 1, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((count / 200) * 100, 100)}
                    sx={{
                      flex: 1,
                      height: 4,
                      backgroundColor: "rgba(255,255,255,0.1)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#C59324",
                      },
                    }}
                  />
                  <Typography sx={{ color: "#6D7690", fontSize: "0.8rem" }}>
                    Goal: 200
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Right Form ────────────────────────────────────── */}
          <Box sx={{ flex: 0.8, width: "100%" }}>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.04)",
                p: { xs: 2, sm: 3, md: 5 },
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  mb: 4,
                }}
              >
                Add Your Name
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Name */}
                <Box>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name*"
                    variant="outlined"
                    sx={inputStyles}
                  />
                  {hasSubmitted && errors.name && (
                    <FormHelperText sx={{ color: "#ff6b6b", ml: 0 }}>
                      {errors.name}
                    </FormHelperText>
                  )}
                </Box>

                {/* Email */}
                <Box>
                  <TextField
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address*"
                    variant="outlined"
                    sx={inputStyles}
                  />
                  {hasSubmitted && errors.email && (
                    <FormHelperText sx={{ color: "#ff6b6b", ml: 0 }}>
                      {errors.email}
                    </FormHelperText>
                  )}
                </Box>

                {/* Phone */}
                <Box>
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      // Allow only numbers and max 10 digits
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);

                      setFormData((prev) => ({
                        ...prev,
                        phone: value,
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        phone: "",
                      }));
                    }}
                    placeholder="Phone (optional)"
                    variant="outlined"
                    sx={inputStyles}
                    slotProps={{
                      htmlInput: {
                        maxLength: 10,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      },
                    }}
                  />
                  {hasSubmitted && errors.phone && (
                    <FormHelperText sx={{ color: "#ff6b6b", ml: 0 }}>
                      {errors.phone}
                    </FormHelperText>
                  )}
                </Box>

                {/* Zip Code */}
                <Box>
                  <TextField
                    fullWidth
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="Zip Code*"
                    variant="outlined"
                    sx={inputStyles}
                    slotProps={{
                      htmlInput: {
                        maxLength: 6,
                      },
                    }}
                  />
                  {hasSubmitted && errors.zipCode && (
                    <FormHelperText sx={{ color: "#ff6b6b", ml: 0 }}>
                      {errors.zipCode}
                    </FormHelperText>
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#D32F2F",
                    color: "#fff",
                    py: 2,
                    fontWeight: 700,
                    fontSize: "1rem",
                    textTransform: "none",
                    mt: 1,
                    "&:hover": { backgroundColor: "#B71C1C" },
                  }}
                >
                  {loading ? "Submitting..." : "Sign the Petition →"}
                </Button>

                {/* ── Inline success/error — right below button ── */}
                {inlineMsg.show && (
                  <Alert
                    severity={inlineMsg.severity}
                    variant="filled"
                    onClose={() =>
                      setInlineMsg((prev) => ({ ...prev, show: false }))
                    }
                    sx={{ mt: 1 }}
                  >
                    {inlineMsg.text}
                  </Alert>
                )}

                <Typography
                  sx={{
                    color: "#6D7690",
                    fontSize: "0.75rem",
                    textAlign: "center",
                    mt: 1,
                    lineHeight: 1.5,
                    maxWidth: "280px",
                    mx: "auto",
                  }}
                >
                  Your information is kept private and used only for campaign
                  communication.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "#C59324" },
    "&.Mui-focused fieldset": { borderColor: "#C59324" },
  },
  "& input::placeholder": { color: "#9BA7C0", opacity: 0.8 },
};

export default PetitionSection;
